import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase-config';
import { adminServices } from '../services/adminServices';
import { useUser } from '../UserContext';

const STATUS_FILTERS = ['all', 'open', 'triaged', 'awaiting_reply', 'resolved'];

function formatTimestamp(value) {
  try {
    const date = value?.toDate ? value.toDate() : value instanceof Date ? value : null;
    if (!date) return '—';
    return date.toLocaleString();
  } catch {
    return '—';
  }
}

function StatusPill({ status }) {
  const palette =
    status === 'resolved'
      ? { bg: 'rgba(16, 185, 129, 0.12)', border: 'rgba(16, 185, 129, 0.35)', text: '#a7f3d0' }
      : status === 'triaged'
        ? { bg: 'rgba(59, 130, 246, 0.12)', border: 'rgba(59, 130, 246, 0.35)', text: '#bfdbfe' }
        : status === 'awaiting_reply'
          ? { bg: 'rgba(251, 191, 36, 0.12)', border: 'rgba(251, 191, 36, 0.35)', text: '#fde68a' }
          : { bg: 'rgba(239, 68, 68, 0.12)', border: 'rgba(239, 68, 68, 0.32)', text: '#fecaca' };

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: 999,
        padding: '5px 10px',
        fontSize: '0.74rem',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        color: palette.text,
      }}
    >
      {String(status || 'open').replace('_', ' ')}
    </span>
  );
}

function compactObject(input) {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return input;
  return Object.fromEntries(
    Object.entries(input).filter(([, value]) => value != null && value !== '' && value !== '—')
  );
}

function buildLiveSchoolSnapshot(data) {
  if (!data) return null;
  return compactObject({
    officialName: data.officialName || null,
    workflowStep: data.workflowStep || null,
    qaPhase: data.qaPhase || null,
    status: data.status || null,
    paymentStatus: data.paymentStatus || null,
    accreditationLevel: data.accreditationLevel || null,
    assignedAuditorUid: data.assignedAuditorUid || null,
    currentLanguage: data.currentLanguage || data.signupLanguage || null,
    finalReportIssuedAt: formatTimestamp(data.finalReportIssuedAt),
    lastWorkflowUpdate: formatTimestamp(data.lastWorkflowUpdate),
    lastLanguageUpdateAt: formatTimestamp(data.lastLanguageUpdateAt),
    renewalRequestType: data.renewalRequest?.requestType || null,
    renewalRequestStatus: data.renewalRequest?.status || null,
  });
}

function buildLiveReportSnapshot(data) {
  if (!data) return null;
  return compactObject({
    qaPhase: data.meta?.qaPhase || null,
    authorUid: data.meta?.authorUid || null,
    overallMaturityScore:
      data.meta?.overallMaturityScore == null ? null : data.meta.overallMaturityScore,
    lastEditedAt: formatTimestamp(data.meta?.lastEditedAt),
    publishedAt: formatTimestamp(data.meta?.publishedAt),
    peerReviewSigned: data.certification?.peerReviewSigned === true,
    peerReviewerUid: data.certification?.peerReviewerUid || null,
    refinery: compactObject({
      evidenceStep: data.meta?.refinery?.evidence?.step ?? null,
      surveyStep: data.meta?.refinery?.survey?.step ?? null,
    }),
  });
}

function buildLiveStaffSnapshot(data) {
  if (!data) return null;
  return compactObject({
    displayName: data.displayName || null,
    email: data.email || null,
    role: data.role || null,
    isApproved: data.isApproved === true,
    canUseSupportAssistant: data.permissions?.canUseSupportAssistant === true,
    canPeerReview: data.permissions?.canPeerReview === true,
    canPublish: data.permissions?.canPublish === true,
    canManageStaff: data.permissions?.canManageStaff === true,
    updatedAt: formatTimestamp(data.updatedAt),
    joinedAt: formatTimestamp(data.joinedAt),
  });
}

function snapshotTone(status) {
  if (status === 'ready') return '#34d399';
  if (status === 'loading') return '#fbbf24';
  if (status === 'missing') return '#f87171';
  return '#94a3b8';
}

function SnapshotStatusPill({ label, status, updatedAt }) {
  const tone = snapshotTone(status);
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '7px 10px',
        borderRadius: 999,
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.07)',
        fontSize: '0.76rem',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 9,
          height: 9,
          borderRadius: '50%',
          background: tone,
          boxShadow: `0 0 0 2px ${tone}22`,
          flex: '0 0 auto',
        }}
      />
      <span style={{ color: 'white' }}>{label}</span>
      <span style={{ opacity: 0.55, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {status}
      </span>
      {updatedAt ? <span style={{ opacity: 0.62 }}>· {formatTimestamp(updatedAt)}</span> : null}
    </div>
  );
}

export default function AdminSupportAssistant({ onOpenRegistryIssue, onOpenReportIssue }) {
  const { user, permissions } = useUser();
  const [issues, setIssues] = useState([]);
  const [selectedIssueId, setSelectedIssueId] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isSaving, setIsSaving] = useState(false);
  const [liveSchoolSnapshot, setLiveSchoolSnapshot] = useState(null);
  const [liveReportSnapshot, setLiveReportSnapshot] = useState(null);
  const [liveStaffSnapshot, setLiveStaffSnapshot] = useState(null);
  const [liveSnapshotStatus, setLiveSnapshotStatus] = useState({ school: 'idle', report: 'idle', staff: 'idle' });
  const [liveSnapshotUpdatedAt, setLiveSnapshotUpdatedAt] = useState({ school: null, report: null, staff: null });

  useEffect(() => {
    if (!permissions?.canUseSupportAssistant) return undefined;
    const q = query(collection(db, 'supportIssues'), orderBy('updatedAt', 'desc'));
    return onSnapshot(q, (snapshot) => {
      const rows = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
      setIssues(rows);
      setSelectedIssueId((current) => current || rows[0]?.id || '');
    });
  }, [permissions?.canUseSupportAssistant]);

  const filteredIssues = useMemo(() => {
    if (statusFilter === 'all') return issues;
    return issues.filter((issue) => String(issue.status || 'open') === statusFilter);
  }, [issues, statusFilter]);

  const selectedIssue =
    filteredIssues.find((issue) => issue.id === selectedIssueId) ||
    issues.find((issue) => issue.id === selectedIssueId) ||
    filteredIssues[0] ||
    null;

  useEffect(() => {
    if (!selectedIssue && filteredIssues.length > 0) {
      setSelectedIssueId(filteredIssues[0].id);
    }
  }, [filteredIssues, selectedIssue]);

  useEffect(() => {
    const schoolDocId = selectedIssue?.schoolId || null;
    const reportDocId = selectedIssue?.reportId || selectedIssue?.schoolId || null;

    setLiveSchoolSnapshot(null);
    setLiveReportSnapshot(null);
    setLiveStaffSnapshot(null);
    setLiveSnapshotStatus({
      school: schoolDocId ? 'loading' : 'idle',
      report: reportDocId ? 'loading' : 'idle',
      staff: 'idle',
    });
    setLiveSnapshotUpdatedAt({ school: null, report: null, staff: null });

    const unsubs = [];

    if (schoolDocId) {
      unsubs.push(
        onSnapshot(doc(db, 'schools', schoolDocId), (snap) => {
          setLiveSchoolSnapshot(snap.exists() ? buildLiveSchoolSnapshot(snap.data()) : null);
          setLiveSnapshotStatus((prev) => ({ ...prev, school: snap.exists() ? 'ready' : 'missing' }));
          setLiveSnapshotUpdatedAt((prev) => ({ ...prev, school: new Date() }));
        })
      );
    }

    if (reportDocId) {
      unsubs.push(
        onSnapshot(doc(db, 'reports', reportDocId), (snap) => {
          setLiveReportSnapshot(snap.exists() ? buildLiveReportSnapshot(snap.data()) : null);
          setLiveSnapshotStatus((prev) => ({ ...prev, report: snap.exists() ? 'ready' : 'missing' }));
          setLiveSnapshotUpdatedAt((prev) => ({ ...prev, report: new Date() }));
        })
      );
    }

    return () => {
      unsubs.forEach((unsubscribe) => unsubscribe());
    };
  }, [selectedIssue?.schoolId, selectedIssue?.reportId]);

  useEffect(() => {
    const assignedStaffUid = liveSchoolSnapshot?.assignedAuditorUid || null;
    setLiveStaffSnapshot(null);
    setLiveSnapshotStatus((prev) => ({
      ...prev,
      staff: assignedStaffUid ? 'loading' : 'idle',
    }));
    setLiveSnapshotUpdatedAt((prev) => ({ ...prev, staff: null }));

    if (!assignedStaffUid) return undefined;

    return onSnapshot(doc(db, 'staff', assignedStaffUid), (snap) => {
      setLiveStaffSnapshot(snap.exists() ? buildLiveStaffSnapshot(snap.data()) : null);
      setLiveSnapshotStatus((prev) => ({ ...prev, staff: snap.exists() ? 'ready' : 'missing' }));
      setLiveSnapshotUpdatedAt((prev) => ({ ...prev, staff: new Date() }));
    });
  }, [liveSchoolSnapshot?.assignedAuditorUid]);

  if (!permissions?.canUseSupportAssistant) return null;

  const changeStatus = async (status) => {
    if (!selectedIssue) return;
    setIsSaving(true);
    try {
      await adminServices.updateSupportIssue(
        selectedIssue.id,
        {
          status,
          handledBy: {
            uid: user?.uid || null,
            displayName: user?.displayName || user?.email || 'IFIP Staff',
          },
        },
        user?.displayName || user?.email || 'IFIP Staff'
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="admin-support-layout">
      <section
        style={{
          background: 'rgba(15, 23, 42, 0.6)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 18,
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: 18, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <h3 style={{ margin: 0, color: 'white' }}>Support queue</h3>
              <p style={{ margin: '6px 0 0 0', color: 'rgba(255,255,255,0.62)', fontSize: '0.9rem' }}>
                Reported issues from staff and school-facing surfaces.
              </p>
            </div>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              style={{
                background: '#0f172a',
                border: '1px solid rgba(255,255,255,0.1)',
                color: 'white',
                borderRadius: 10,
                padding: '8px 10px',
              }}
            >
              {STATUS_FILTERS.map((value) => (
                <option key={value} value={value}>
                  {value === 'all' ? 'All statuses' : value.replace('_', ' ')}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
          {filteredIssues.length === 0 ? (
            <div style={{ padding: 18, color: 'rgba(255,255,255,0.7)' }}>No issues in this view.</div>
          ) : (
            filteredIssues.map((issue) => {
              const isActive = issue.id === selectedIssue?.id;
              return (
                <button
                  key={issue.id}
                  type="button"
                  onClick={() => setSelectedIssueId(issue.id)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    background: isActive ? 'rgba(19, 138, 138, 0.12)' : 'transparent',
                    border: 'none',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                    padding: 16,
                    cursor: 'pointer',
                    color: 'white',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                    <div style={{ fontWeight: 700 }}>
                      {issue.schoolName || issue.reporter?.displayName || issue.route || issue.id}
                    </div>
                    <StatusPill status={issue.status || 'open'} />
                  </div>
                  <div style={{ marginTop: 8, fontSize: '0.86rem', opacity: 0.74, lineHeight: 1.45 }}>
                    {issue.description || 'No description provided.'}
                  </div>
                  <div style={{ marginTop: 8, fontSize: '0.75rem', opacity: 0.55 }}>
                    {(issue.issueType || 'other').toUpperCase()} · {issue.sourceSurface || 'unknown'} · {formatTimestamp(issue.createdAt)}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </section>

      <section
        style={{
          background: 'rgba(15, 23, 42, 0.6)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 18,
          padding: 20,
          color: 'white',
          minHeight: 460,
        }}
      >
        {!selectedIssue ? (
          <div style={{ color: 'rgba(255,255,255,0.7)' }}>Select an issue to inspect the reported context.</div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.55 }}>
                  {selectedIssue.issueType || 'other'} · {selectedIssue.sourceSurface || 'unknown'}
                </div>
                <h3 style={{ margin: '8px 0 0 0' }}>
                  {selectedIssue.schoolName || selectedIssue.reporter?.displayName || 'Reported issue'}
                </h3>
                <p style={{ margin: '10px 0 0 0', opacity: 0.78, lineHeight: 1.55 }}>
                  {selectedIssue.description || 'No description provided.'}
                </p>
              </div>
              <StatusPill status={selectedIssue.status || 'open'} />
            </div>

            <div style={{ marginTop: 20, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button type="button" className="btn-nudge" disabled={isSaving} onClick={() => changeStatus('triaged')}>
                Mark triaged
              </button>
              <button type="button" className="btn-nudge" disabled={isSaving} onClick={() => changeStatus('awaiting_reply')}>
                Awaiting reply
              </button>
              <button type="button" className="btn-activate-text" disabled={isSaving} onClick={() => changeStatus('resolved')}>
                Mark resolved
              </button>
              <button type="button" className="btn-secondary" disabled={isSaving} onClick={() => changeStatus('open')}>
                Reopen
              </button>
              {selectedIssue.schoolId ? (
                <button type="button" className="btn-secondary" onClick={() => onOpenRegistryIssue?.(selectedIssue.schoolId)}>
                  Open school record
                </button>
              ) : null}
              {selectedIssue.schoolId ? (
                <button type="button" className="btn-secondary" onClick={() => onOpenReportIssue?.(selectedIssue.schoolId)}>
                  Open report
                </button>
              ) : null}
            </div>

            <div style={{ marginTop: 24, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              {[
                ['Reporter', selectedIssue.reporter?.displayName],
                ['Reporter email', selectedIssue.reporter?.email],
                ['Reporter role', selectedIssue.reporter?.role],
                ['Attempted action', selectedIssue.attemptedAction],
                ['Visible error', selectedIssue.errorSummary],
                ['Route', selectedIssue.route],
                ['School ID', selectedIssue.schoolId],
                ['Report ID', selectedIssue.reportId],
                ['Workflow step', selectedIssue.workflowStep],
                ['QA phase', selectedIssue.qaPhase],
                ['Language', selectedIssue.language],
                ['Created', formatTimestamp(selectedIssue.createdAt)],
                ['Updated', formatTimestamp(selectedIssue.updatedAt)],
              ]
                .filter(([, value]) => value != null && value !== '')
                .map(([label, value]) => (
                  <div
                    key={label}
                    style={{
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.06)',
                      borderRadius: 14,
                      padding: '12px 14px',
                    }}
                  >
                    <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.55 }}>
                      {label}
                    </div>
                    <div style={{ marginTop: 6, lineHeight: 1.45, wordBreak: 'break-word' }}>{String(value)}</div>
                  </div>
                ))}
            </div>

            <div style={{ marginTop: 24 }}>
              <h4 style={{ margin: '0 0 10px 0' }}>Latest live snapshot</h4>
              <p style={{ margin: '0 0 14px 0', color: 'rgba(255,255,255,0.62)', fontSize: '0.88rem' }}>
                Auto-updating from the current Firestore school/report documents, so support sees the latest state as well as the original report.
              </p>
              <div
                style={{
                  marginBottom: 14,
                  padding: '12px 14px',
                  borderRadius: 14,
                  background: 'rgba(19, 138, 138, 0.08)',
                  border: '1px solid rgba(19, 138, 138, 0.24)',
                  color: 'rgba(255,255,255,0.88)',
                  fontSize: '0.86rem',
                  lineHeight: 1.5,
                }}
              >
                Passive diagnostic mode: this workspace only reads the current Firestore records linked to the issue. It does not edit code, change school/report workflow, or run any privileged actions from this panel.
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                <SnapshotStatusPill
                  label="School"
                  status={liveSnapshotStatus.school}
                  updatedAt={liveSnapshotUpdatedAt.school}
                />
                <SnapshotStatusPill
                  label="Report"
                  status={liveSnapshotStatus.report}
                  updatedAt={liveSnapshotUpdatedAt.report}
                />
                <SnapshotStatusPill
                  label="Assigned staff"
                  status={liveSnapshotStatus.staff}
                  updatedAt={liveSnapshotUpdatedAt.staff}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
                <div
                  style={{
                    background: '#020617',
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: 16,
                    padding: 16,
                  }}
                >
                  <div style={{ fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.55 }}>
                    Live school doc
                  </div>
                  <div style={{ marginTop: 8, marginBottom: 10, fontSize: '0.8rem', opacity: 0.66 }}>
                    {liveSnapshotStatus.school === 'loading'
                      ? 'Loading latest school snapshot...'
                      : liveSnapshotStatus.school === 'missing'
                        ? 'No current school doc found.'
                        : liveSnapshotStatus.school === 'idle'
                          ? 'No school id captured for this issue.'
                          : 'Current school state'}
                  </div>
                  <pre
                    style={{
                      margin: 0,
                      color: '#cbd5e1',
                      fontSize: '0.78rem',
                      lineHeight: 1.45,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {JSON.stringify(liveSchoolSnapshot || {}, null, 2)}
                  </pre>
                </div>

                <div
                  style={{
                    background: '#020617',
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: 16,
                    padding: 16,
                  }}
                >
                  <div style={{ fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.55 }}>
                    Live report doc
                  </div>
                  <div style={{ marginTop: 8, marginBottom: 10, fontSize: '0.8rem', opacity: 0.66 }}>
                    {liveSnapshotStatus.report === 'loading'
                      ? 'Loading latest report snapshot...'
                      : liveSnapshotStatus.report === 'missing'
                        ? 'No current report doc found.'
                        : liveSnapshotStatus.report === 'idle'
                          ? 'No report id captured for this issue.'
                          : 'Current report state'}
                  </div>
                  <pre
                    style={{
                      margin: 0,
                      color: '#cbd5e1',
                      fontSize: '0.78rem',
                      lineHeight: 1.45,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {JSON.stringify(liveReportSnapshot || {}, null, 2)}
                  </pre>
                </div>

                <div
                  style={{
                    background: '#020617',
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: 16,
                    padding: 16,
                  }}
                >
                  <div style={{ fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.55 }}>
                    Live assigned staff doc
                  </div>
                  <div style={{ marginTop: 8, marginBottom: 10, fontSize: '0.8rem', opacity: 0.66 }}>
                    {liveSnapshotStatus.staff === 'loading'
                      ? 'Loading assigned staff snapshot...'
                      : liveSnapshotStatus.staff === 'missing'
                        ? 'Assigned staff doc not found.'
                        : liveSnapshotStatus.staff === 'idle'
                          ? 'No assigned staff captured for this issue.'
                          : 'Current assigned staff state'}
                  </div>
                  <pre
                    style={{
                      margin: 0,
                      color: '#cbd5e1',
                      fontSize: '0.78rem',
                      lineHeight: 1.45,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {JSON.stringify(liveStaffSnapshot || {}, null, 2)}
                  </pre>
                </div>
              </div>
            </div>

            <div style={{ marginTop: 24 }}>
              <h4 style={{ margin: '0 0 10px 0' }}>Captured context</h4>
              <pre
                style={{
                  margin: 0,
                  padding: 16,
                  borderRadius: 16,
                  background: '#020617',
                  border: '1px solid rgba(255,255,255,0.06)',
                  color: '#cbd5e1',
                  fontSize: '0.8rem',
                  lineHeight: 1.5,
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {JSON.stringify(selectedIssue.context || {}, null, 2)}
              </pre>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
